import * as z from "zod";

/**
 * The deployment history for a single extension target — every artifact that
 * has been live for it, newest first, with who deployed it and when.
 *
 * The target is addressed in friendly terms (rate table id, or flow + step id);
 * the handler derives the underlying natural key via the deployment-target
 * helpers so callers don't have to know the key format.
 */
export const getTargetDeployments = {
  name: "getTargetDeployments" as const,
  input: z.union([
    z.object({
      targetType: z.literal("checkout.shipping-method.custom-rate-table"),
      rateTableId: z.string(),
    }),
    z.object({
      targetType: z.literal("oms-automation.code-step"),
      flowId: z.string(),
      stepId: z.string(),
    }),
    // Per-team singleton; the handler derives the key from the caller's team.
    z.object({ targetType: z.literal("returns.exchange-order.creation") }),
    // Any RMA function: `rmaTargetType` is its real target type, resolved
    // through the RMA function registry.
    z.object({
      targetType: z.literal("rma-function"),
      rmaTargetType: z.string(),
      integrationId: z.string(),
    }),
  ]),
  output: z.object({
    deployments: z.array(
      z.object({
        artifactId: z.string(),
        actor: z
          .union([
            // `name` is the deployer's resolved display name (or null when the
            // user lookup misses).
            z.object({
              type: z.literal("userId"),
              userId: z.string(),
              name: z.string().nullable(),
            }),
            z.object({ type: z.literal("admin"), email: z.string() }),
            z.object({ type: z.literal("agent"), agentId: z.string() }),
            z.object({ type: z.literal("system"), source: z.string() }),
          ])
          .nullable(),
        deployedAt: z.string(),
      }),
    ),
  }),
};
